import React from "react";
import { CustomDataGrid, SectionPaper } from '../../../components';
import { GridActionsCellItem, GridColTypeDef, GridEventListener, GridRenderCellParams, GridRenderEditCellParams, GridRowEditStopReasons, GridRowId, GridRowModel, GridRowModes, GridRowModesModel, useGridApiContext, useGridApiRef } from '@mui/x-data-grid-premium';
import { Box, InputBase, InputBaseProps, Paper, Stack, styled, Tooltip, Typography } from '@mui/material';
import copy from 'fast-copy';
import { AutoAwesome, Cancel, Edit, Lock, Save } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { LoadingButton } from "@mui/lab";
import { hexToGrayscale } from "../../../utils";
import { ExpandableCell } from "../../../components/customDataGrid/components/expandableCell";

function EditTextarea(props: GridRenderEditCellParams<any, string>) {
    const { id, field, value, colDef, hasFocus } = props;
    const [valueState, setValueState] = React.useState(value);
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>();
    const [inputRef, setInputRef] = React.useState<HTMLInputElement | null>(null);
    const apiRef = useGridApiContext();

    React.useLayoutEffect(() => {
        if (hasFocus && inputRef) {
            inputRef.focus();
        }
    }, [hasFocus, inputRef]);

    const handleRef = React.useCallback((el: HTMLElement | null) => {
        setAnchorEl(el);
    }, []);

    const handleChange = React.useCallback<NonNullable<InputBaseProps['onChange']>>(
        (event) => {
            event.preventDefault();
            event.stopPropagation();
            const newValue = event.target.value;
            setValueState(newValue);
            apiRef.current.setEditCellValue(
                { id, field, value: newValue, debounceMs: 200 },
                event,
            );
        },
        [apiRef, field, id],
    );

    return (
        <div style={{ position: 'relative', alignSelf: 'flex-start' }}>
            <div
                ref={handleRef}
                style={{
                    height: 1,
                    width: colDef.computedWidth,
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                }}
            />
            {anchorEl && (
                <Paper elevation={0} sx={{ p: 0.5, minWidth: colDef.computedWidth }}>
                    <InputBase
                        multiline
                        rows={2}
                        value={valueState}
                        sx={{ width: '100%' }}
                        onChange={handleChange}
                        inputRef={(ref) => setInputRef(ref)}
                    />
                </Paper>
            )}
        </div>
    );
}

const multilineColumn: GridColTypeDef = {
    type: 'string',
    renderEditCell: (params) => <EditTextarea {...params} />,
};

export const StyledDataGrid = styled(CustomDataGrid)(() => ({
    // borderLeftWidth: 0,
    // borderRightWidth: 0,
}));

const CustomTable = ({ data, state, updateState, index, preview = false, onUpdate, includeCheckbox = false, searchResponse, autoPopulate, onAutoPopulate }: any) => {

    const { enqueueSnackbar } = useSnackbar();
    const apiRef = useGridApiRef();

    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const [isUpdateRequired, setUpdateRequired] = React.useState(false);

    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        if (Object.keys(rowModesModel || {}).length === 0) {
            setRowModesModel(newRowModesModel);
        }
    };

    const handleEditClick = (id: GridRowId) => () => {
        const field = Object.keys(rowModesModel || {})?.[0];
        if (!field) {
            setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
        }
    };

    const handleSaveClick = (id: GridRowId) => () => {
        const field = Object.keys(rowModesModel?.[id] || {})?.[0];
        if (field) {
            setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
            setUpdateRequired(true);
        }
    };

    const handleCancelClick = (id: GridRowId) => () => {
        const field = Object.keys(rowModesModel?.[id] || {})?.[0];
        if (field) {
            setRowModesModel({
                ...rowModesModel,
                [id]: { mode: GridRowModes.View, ignoreModifications: true },
            });
        }
    };

    const handleProcessRowUpdateError = React.useCallback((error: Error) => {
        console.error("error:", error);
        enqueueSnackbar(error.message, { variant: "error" });
        // eslint-disable-next-line
    }, [])

    const processRowUpdate = (newRow: GridRowModel, oldRow: GridRowModel, index: number, state: any) => {
        let updatedRow: any = { ...newRow, isNew: false };
        if (!preview && autoPopulate) {
            let newCategories = copy(state);
            let rows = Object.values(apiRef.current.state.rows.dataRowIdToModelLookup || {});
            if (rows?.length <= 0) {
                rows = newCategories[index].definition.rows;
            }
            const colIndex = rows.findIndex((r) => r.id === updatedRow.id)
            updatedRow = {
                ...oldRow,
                ...Object.keys(updatedRow).reduce((acc: any, key) => {
                    if ((key in updatedRow) || (key in oldRow)) {
                        acc[key] = updatedRow[key] ?? oldRow[key];
                    }
                    return acc;
                }, {}),
            };
            rows[colIndex] = updatedRow
            newCategories[index].definition.rows = rows;
            updateState(newCategories);
        }
        return updatedRow;
    };

    const handleRowEditStop: GridEventListener<"rowEditStop"> = (
        params,
        event
    ) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    React.useEffect(() => {
        let key = Object.keys(rowModesModel || {})?.[0];
        if (key) {
            if (rowModesModel[key]?.mode === "view") {
                setRowModesModel({});
            }
        }
        // eslint-disable-next-line
    }, [rowModesModel]);

    const Columns = React.useCallback((table: any) => {
        let cols = [
            ...table.definition?.columns?.map((_: any, idx: number) => {
                const notesCell = autoPopulate ? {
                    renderCell: (params: GridRenderCellParams) => <ExpandableCell {...params} />,
                } : {};
                return {
                    ..._,
                    ...notesCell,
                    sortable: false,
                    hideSortIcons: true,
                    disableColumnMenu: true,
                    renderHeader: () => {
                        return (
                            (<Stack
                                direction={"row"}
                                spacing={1}
                                key={idx}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    bgcolor: `${data?.header_color ? `${data?.header_color} !important` : 'rgb(230, 229, 229) !important'}`,
                                    width: "100% !important",
                                    px: 1
                                }}>
                                <strong>{_.header_name}</strong>
                                {!_?.editable && <Lock color="error" fontSize="small" />}
                            </Stack>)
                        );
                    },
                    ...(autoPopulate ? multilineColumn : {}),
                };
            })
        ]

        if (autoPopulate && !preview) {
            cols.unshift({
                field: 'actions',
                type: 'actions',
                headerName: 'Actions',
                width: 100,
                cellClassName: 'actions',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                resizable: false,
                renderHeader: () => {
                    return (
                        <Box sx={{
                            bgcolor: `${data?.header_color ? `${data?.header_color} !important` : 'rgb(230, 229, 229) !important'}`,
                            width: "100% !important",
                            px: 1
                        }}>
                            <strong>Actions</strong>
                        </Box>
                    )
                },
                getActions: (e: any) => {

                    const { id, row, columns } = e;

                    const isInEditMode =
                        rowModesModel?.[id]?.mode === GridRowModes.Edit;

                    if (isInEditMode) {
                        return [
                            <GridActionsCellItem
                                disableRipple
                                icon={<Save sx={{ height: "36px" }} />}
                                label="Save"
                                sx={{
                                    color: "primary.main",
                                }}
                                onClick={handleSaveClick(id)}
                            />,
                            <GridActionsCellItem
                                disableRipple
                                icon={<Cancel />}
                                label="Cancel"
                                className="textPrimary"
                                onClick={handleCancelClick(id)}
                                color="inherit"
                            />,
                        ];
                    }

                    let actions = [
                        <GridActionsCellItem
                            disableRipple
                            icon={<Edit sx={{ height: "36px" }} />}
                            label="Edit"
                            className="textPrimary"
                            onClick={handleEditClick(id)}
                            color="inherit"
                        />
                    ]

                    actions.push(
                        <GridActionsCellItem
                            disableRipple
                            icon={<Tooltip arrow placement="top" title='Auto Populate Response and Notes'>
                                <AutoAwesome color="info" sx={{ height: "36px" }} />
                            </Tooltip>}
                            label="Auto Populate"
                            className="textPrimary"
                            onClick={() => onAutoPopulate(columns, [row])}
                            color="inherit"
                        />
                    );

                    return actions;
                }
            })
        }

        if (includeCheckbox) {
            cols.shift();
        }

        if (searchResponse?.hasScore) {
            cols.push({
                field: "score",
                headerName: "Score",
                width: 350,
                renderHeader: () => {
                    return (
                        <Box sx={{ bgcolor: `${data?.header_color ? `${data?.header_color} !important` : 'rgb(230, 229, 229) !important'}`, width: "100% !important", px: 1 }}>
                            <strong>Score</strong>
                        </Box>
                    )
                },
                renderCell: (params: GridRenderCellParams) => {
                    let key = data?.definition?.columns[0]?.field;
                    const d = searchResponse?.response?.matching_fields?.[params.row?.[key]];
                    if (params.rowNode.type === "group") {
                        return params.value;
                    }
                    return (
                        <Typography
                            variant="body2"
                            sx={{
                                color: d?.score === 1 ? "green" : "primary.main",
                                textDecoration: d?.score ? "underline" : "none",
                                cursor: d?.score ? "pointer" : 'default'
                            }}
                            onClick={() => searchResponse?.onOpenMatches({ ...d, title: params.row?.[key] })}
                        >
                            {d?.score ? (d?.score * 100).toFixed(2) : "No Match Found"}
                        </Typography>
                    );
                },
            },)
        }

        return table.definition?.columns.length > 0 ? cols : [];
        // eslint-disable-next-line
    }, [JSON.stringify(data), rowModesModel, JSON.stringify(searchResponse)]);

    const getRows = () => {
        if (!searchResponse) {
            return data.definition.rows;
        } else {
            const keys = Object.keys(searchResponse?.response?.matching_fields || {});
            return data.definition.rows.filter((r: any) => {
                let values = Object.values(r)
                let res = keys.map(k => values.includes(k))
                return res.filter(Boolean).length > 0;
            });
        }
    }

    const rows = React.useMemo(() => {
        return getRows();
        // eslint-disable-next-line
    }, [data.definition.rows, searchResponse])


    React.useEffect(() => {
        if (isUpdateRequired) {
            onUpdate(() => setUpdateRequired(false));
        }
        // eslint-disable-next-line
    }, [data?.definition?.rows, isUpdateRequired]);

    React.useEffect(() => {
        if (!apiRef?.current) return;

        let previousRows: any = null; // Track the last state of rows

        const handleStateChange: GridEventListener<'stateChange'> = (newState) => {
            if (!apiRef?.current) return;

            // Get the current rows state
            const currentRowsState = newState.rows?.dataRowIdToModelLookup;
            if (!currentRowsState) return;

            // Convert lookup object to an array
            const updatedRows = Object.values(currentRowsState);

            // Check if rows have actually changed
            if (
                previousRows &&
                JSON.stringify(previousRows) === JSON.stringify(updatedRows)
            ) {
                return; // No changes, skip state update
            }

            if (!preview && !autoPopulate && previousRows) {
                setUpdateRequired(true);
            }

            // Update the tracked state
            previousRows = updatedRows;

            const newCategories = JSON.parse(JSON.stringify(state));
            newCategories[index].definition.rows = updatedRows;
            updateState(newCategories);
        };

        const unsubscribeStateChange = apiRef.current.subscribeEvent(
            'stateChange',
            handleStateChange
        );

        return () => {
            if (unsubscribeStateChange) unsubscribeStateChange();
        };
        // eslint-disable-next-line
    }, [apiRef, index]);

    // eslint-disable-next-line
    const COLUMNS = React.useMemo(() => Columns(data), [data, rowModesModel, JSON.stringify(searchResponse)]);

    if (rows?.length <= 0 && searchResponse) {
        return <></>;
    }

    const renderButtons = [];

    if (includeCheckbox) {
        renderButtons.push(<LoadingButton
            fullWidth
            size="medium"
            variant="outlined"
            sx={{ width: 200 }}
            onClick={() => {
                let newCategories = copy(state);
                newCategories[index].rowSelectionModel = [];
                updateState(newCategories);
            }}
        >
            Reset
        </LoadingButton>)
    }

    if (autoPopulate) {
        renderButtons.push(<LoadingButton
            size="small"
            variant="contained"
            onClick={() => {
                onAutoPopulate(data.definition?.columns ?? [], rows)
            }}
            startIcon={<AutoAwesome />}
            color="primary"
        >
            Auto Populate
        </LoadingButton>)
    }

    const tableProps = (preview || autoPopulate) ? {
        onRowEditStop: handleRowEditStop,
        rowModesModel: rowModesModel,
        onRowModesModelChange: handleRowModesModelChange,
    } : {};


    return (
        <SectionPaper
            title={data?.name ?? ""}
            subTitle={`${data?.description ? `(${data?.description})` : ""}`}
            contentPadding={1}
            renderButtons={renderButtons}
        >
            <StyledDataGrid
                apiRef={apiRef}
                saveLocal={false}
                rows={rows}
                columns={COLUMNS}
                loading={false}
                showToolbar={false}
                getRowId={(row) => row.id}
                processRowUpdate={(newRow, oldRow) => processRowUpdate(newRow, oldRow, index, state)}
                // onRowEditStop={handleRowEditStop}
                // rowModesModel={rowModesModel}
                // onRowModesModelChange={handleRowModesModelChange}
                {...tableProps}
                onCellDoubleClick={(e) => (e.isEditable = (preview || autoPopulate) ? false : true)}
                onCellKeyDown={(e) => (e.isEditable = (preview || autoPopulate) ? false : true)}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                getRowHeight={() => 'auto'}
                hideFooter
                pagination={false}
                checkboxSelection={includeCheckbox}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    if (includeCheckbox) {
                        let newCategories = copy(state);
                        newCategories[index].rowSelectionModel = newRowSelectionModel;
                        updateState(newCategories);
                    }
                }}
                disableClipboardPaste={(preview || autoPopulate) ? true : false}
                rowSelectionModel={includeCheckbox ? state[index].rowSelectionModel : undefined}
                cellSelection={(preview || autoPopulate) ? false : true}
                editMode={(preview || autoPopulate) ? undefined : "row"}
                classes={{
                    'columnHeaderTitleContainerContent': 'checkListColumnHeaderTitleContent',
                }}
                sx={{
                    '.MuiDataGrid-columnHeaderTitleContainer': {
                        bgcolor: `${data?.header_color ? `${data?.header_color} !important` : 'rgb(230, 229, 229) !important'}`,
                        color: hexToGrayscale(data?.header_color) > 150 ? "#000000" : "#FFFFFF",
                        width: "100% !important",
                    },
                    '.MuiDataGrid-columnHeaderTitleContainerContent': {
                        bgcolor: `${data?.header_color ? `${data?.header_color} !important` : 'rgb(230, 229, 229) !important'}`,
                        color: hexToGrayscale(data?.header_color) > 150 ? "#000000" : "#FFFFFF",
                        width: "100% !important",
                    },
                    '.MuiDataGrid-columnHeader': {
                        bgcolor: `${data?.header_color ? `${data?.header_color} !important` : 'rgb(230, 229, 229) !important'}`,
                        padding: "0 !important",
                        color: hexToGrayscale(data?.header_color) > 150 ? "#000000" : "#FFFFFF",
                    },
                    '.MuiDataGrid-cell': {
                        display: "flex !important",
                        alignItems: "center !important",
                        minHeight: '36px',
                    },
                    position: "relative",
                }}
            />
        </SectionPaper>
    )
}

export default CustomTable;